import styled from '@emotion/styled';
import { forwardRef, useMemo } from 'react';
import type { EmotionScore } from '@hotjar/insights-common/constants/emotions';
import { EmotionScores } from '@hotjar/insights-common/constants/emotions';
import { useTranslation } from '@hotjar/translations';
import type { IconSize } from './icons/settings';
import { iconSizeMap } from './icons/settings';
import { BuddyIcon } from './icons/BuddyIcon';
import { SmileyIcon } from './icons/SmileyIcon';
import { EmojiIcon } from './icons/EmojiIcon';
import { StarIcon } from './icons/StarIcon';

const Container = styled.span`
  isolation: isolate;
`;

export const useEmotionLabels = (): Record<EmotionScore, string> => {
  const { t } = useTranslation('feedback');
  const labels = useMemo<Record<EmotionScore, string>>(() => {
    return {
      0: t('common.emotion.label.hate'),
      1: t('common.emotion.label.dislike'),
      2: t('common.emotion.label.neutral'),
      3: t('common.emotion.label.like'),
      4: t('common.emotion.label.love'),
    };
  }, [t]);

  return labels;
};

interface SharedEmotionIconProps {
  className?: string;
  score: EmotionScore;
  size?: IconSize;
  ariaLabel?: string;
}

interface BaseEmotionIconProps extends SharedEmotionIconProps {
  emotionStyle: 'default' | 'emoji' | 'smiley';
}

interface StarEmotionIconProps extends SharedEmotionIconProps {
  emotionStyle: 'star';
  compactStars?: boolean;
  hideOffStars?: boolean;
}

type EmotionIconProps = BaseEmotionIconProps | StarEmotionIconProps;

export const EmotionIcon = forwardRef<HTMLSpanElement, EmotionIconProps>((props, ref) => {
  const { className, emotionStyle, score, size = 'm', ariaLabel } = props;
  const { t } = useTranslation('feedback');
  const emotionLabels = useEmotionLabels();

  const iconPxSize = iconSizeMap[size];

  function renderIcon(): JSX.Element | null {
    if (emotionStyle === 'default') {
      return <BuddyIcon size={size} score={score} />;
    }
    if (emotionStyle === 'emoji') {
      return <EmojiIcon aria-hidden score={score} size={iconPxSize} />;
    }
    if (emotionStyle === 'smiley') {
      return <SmileyIcon size={size} score={score} />;
    }
    if (props.emotionStyle === 'star') {
      return (
        <>
          {EmotionScores.map((s) =>
            props.hideOffStars && score < s ? null : (
              <StarIcon
                aria-hidden
                index={s}
                isCompact={props.compactStars}
                isOn={score >= s}
                size={iconPxSize}
                key={s}
              />
            ),
          )}
        </>
      );
    }
    return null;
  }

  return (
    <Container
      ref={ref}
      aria-label={ariaLabel || t('common.emotion.label.emotion', { value: emotionLabels[score] })}
      className={className}
      role="img"
    >
      {renderIcon()}
    </Container>
  );
});
